import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, Send, MessageCircle } from "lucide-react";
import { NAV_FULL, SITE } from "@/lib/site";
import { useState } from "react";
import { submitInquiry } from "@/lib/api";
import { toast } from "sonner";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await submitInquiry({ type: "newsletter", email, source_page: "footer" });
      toast.success("Subscribed. Welcome to the curated list.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-charcoal-900 text-white relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 grain-overlay opacity-30" />
      <div className="container-edge pt-24 pb-10 relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Brand & newsletter */}
          <div className="lg:col-span-5">
            <Link to="/" data-testid="footer-logo" className="inline-block">
              <img
                src="/brands/jis-logo-transparent.png"
                alt="Jain Interior Solutions"
                className="h-16 w-auto select-none block"
                draggable={false}
              />
            </Link>
            <p className="mt-6 text-sm text-white/60 max-w-md leading-relaxed">
              India's destination for intelligent living and premium interior solutions. Authorized
              distributor of Fibaro Home Automation.
            </p>
            <div className="mt-8 inline-flex items-center gap-4" data-testid="footer-fibaro-badge">
              <span className="text-[10px] uppercase tracking-[0.24em] text-white/40">Authorized Distributor</span>
              <span className="h-px w-6 bg-white/20" />
              <img
                src="/brands/fibaro-logo-transparent.png"
                alt="Fibaro — a Nice brand"
                className="h-9 w-auto"
                draggable={false}
              />
            </div>
            <form onSubmit={subscribe} className="mt-10" data-testid="newsletter-form">
              <p className="eyebrow text-white/50">Newsletter</p>
              <div className="mt-3 flex items-center border-b border-white/30 focus-within:border-gold transition-colors">
                <input
                  data-testid="newsletter-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="flex-1 bg-transparent py-3 text-sm placeholder:text-white/30 outline-none"
                />
                <button
                  data-testid="newsletter-submit"
                  disabled={loading}
                  className="text-gold hover:text-white transition-colors p-2"
                  aria-label="Subscribe"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>

          {/* Quick links */}
          <div className="lg:col-span-3">
            <p className="eyebrow text-white/50">Navigate</p>
            <ul className="mt-6 space-y-3">
              {NAV_FULL.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-white/70 hover:text-gold transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <p className="eyebrow text-white/50">Visit</p>
            <address className="mt-6 not-italic text-sm text-white/70 leading-relaxed">
              {SITE.name}<br />
              {SITE.address.line1}<br />
              {SITE.address.line2}<br />
              {SITE.address.city}<br />
              {SITE.address.state}, {SITE.address.country}
            </address>
            <div className="mt-6 space-y-3 text-sm">
              <div>
                <p className="text-[9px] uppercase tracking-[0.22em] text-gold mb-1">Fibaro & Home Automation</p>
                <a href={`tel:${SITE.contacts.fibaro.phone.replace(/\s/g, "")}`} className="block text-white/80 hover:text-gold" data-testid="footer-phone-fibaro">
                  {SITE.contacts.fibaro.phone}
                </a>
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-[0.22em] text-gold mb-1">Premium Interior Products</p>
                <a href={`tel:${SITE.contacts.interior.phone.replace(/\s/g, "")}`} className="block text-white/80 hover:text-gold" data-testid="footer-phone-interior">
                  {SITE.contacts.interior.phone}
                </a>
              </div>
              <a href={`mailto:${SITE.email}`} className="block text-white/70 hover:text-gold pt-3 border-t border-white/10" data-testid="footer-email">
                {SITE.email}
              </a>
            </div>
          </div>
        </div>

        {/* Instagram row — split left and right of the page */}
        <div className="mt-20 pt-10 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-6" data-testid="footer-instagram-row">
          <a
            href="https://instagram.com/fibaro.india"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-fibaro"
            aria-label="Fibaro India on Instagram"
            className="group inline-flex items-center gap-4 text-white/80 hover:text-gold transition-colors"
          >
            <span className="h-12 w-12 inline-flex items-center justify-center border border-white/20 group-hover:border-gold transition-colors">
              <Instagram className="h-5 w-5" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-[9px] uppercase tracking-[0.28em] text-white/50 group-hover:text-gold transition-colors">Follow · Instagram</span>
              <span className="text-sm uppercase tracking-[0.22em]">Fibaro India</span>
            </span>
          </a>

          <a
            href="https://instagram.com/jain.interior.solutions"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-interior"
            aria-label="Jain Interior Solutions Hardware on Instagram"
            className="group inline-flex items-center gap-4 text-white/80 hover:text-gold transition-colors sm:flex-row-reverse"
          >
            <span className="h-12 w-12 inline-flex items-center justify-center border border-white/20 group-hover:border-gold transition-colors">
              <Instagram className="h-5 w-5" />
            </span>
            <span className="flex flex-col leading-tight sm:text-right">
              <span className="text-[9px] uppercase tracking-[0.28em] text-white/50 group-hover:text-gold transition-colors">Follow · Instagram</span>
              <span className="text-sm uppercase tracking-[0.22em]">Hardware</span>
            </span>
          </a>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs uppercase tracking-[0.22em] text-white/40">
          <p data-testid="footer-copy">© 2026 Jain Interior Solutions. All Rights Reserved.</p>
          <p>Authorized Fibaro Distributor · Coimbatore, India</p>
        </div>
      </div>
    </footer>
  );
};
